import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useContext } from 'react';
import { toast } from 'sonner';
import { qk } from '@/lib/query';
import * as setupApi from '@/api/setup';
import { SessionContext } from './useSession';
import type { SetupAction, SetupState } from '@/types';

/**
 * The setup guide's state is derived server-side from the team's data, so it
 * is never patched locally: every create mutation invalidates `qk.setup` and
 * the rail refetches. Only runs once a team exists; a person mid-signup has
 * nothing to set up yet.
 *
 * Never runs for someone who joined by invite (see `useSetupApplies`), so
 * every surface that reads `data` (rail, chip, sidebar item, /setup) stays
 * empty for them. Check `useSetupApplies` before treating `isPending` as
 * "still loading": a disabled query is pending forever.
 */
export function useSetup() {
  const applies = useSetupApplies();
  return useQuery({
    queryKey: qk.setup,
    queryFn: ({ signal }) => setupApi.fetchSetup(signal),
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    enabled: applies
  });
}

/** Onboarding is for the person who made the team, never for an invitee. */
export function useSetupApplies(): boolean {
  const session = useContext(SessionContext);
  return !!session?.team && session.isTeamCreator;
}

/**
 * The guide is still part of the app: it applies, its state is loaded and
 * onboarding is not over. Hidden still counts (the sidebar item stays).
 */
export function setupInProgress(state: SetupState | undefined): state is SetupState {
  return !!state && !state.finished;
}

/** hide / show / dismissChip / startOver / skipImport. The response is the new state. */
export function useSetupAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (action: SetupAction) => setupApi.setupAction(action),
    onSuccess(state: SetupState) {
      qc.setQueryData<SetupState>(qk.setup, state);
    },
    onError(error: unknown) {
      toast.error('Could not update the setup guide.', {
        description: error instanceof Error ? error.message : undefined
      });
    }
  });
}
